import { Controller } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import {
  ButtonSecondary,
  ButtonSize,
  formatBalance,
  IconExpand,
  IconLoader,
  Select,
  SelectSize,
} from '@ui/src';
import { NewApplicationStatus } from '../../../../../hoc/new-application/types';
import StepPartBlock from '../../../common/step-part-block';
import InfoRow from '../../../common/info-row';
import SourceAndSiceOfWealthItem from './source-and-size-of-wealth-item';
import useSourceAndSizeOfWealth from '../../hooks/use-source-and-size-of-wealth';
import { SourceAndSiceOfWealthTypes } from '../types';

import cn from '../../styles.module.css';
import commonStyles from '../../../common/styles.module.css';

const SourceAndSizeOfWealth = ({
  sourceOfWealthForIndividual,
  questionnarieControl,
  questionnarieClearErrors,
  questionnarieErrors,
  status,
  isEditable,
  questionnarieSetValue,
  questionnarieFields,
  baseCurrency,
  questionnarieIsLoading,
}: SourceAndSiceOfWealthTypes) => {
  const { sourceOfWealthForIndividualFiltered, totalValue } =
    useSourceAndSizeOfWealth({
      sourceOfWealthForIndividual,
      questionnarieFields,
    });

  return (
    <StepPartBlock title="Source and size of wealth" zIndex={24}>
      {questionnarieIsLoading && (
        <div className={commonStyles.dictionaryLoader}>
          <IconLoader />
        </div>
      )}
      {!questionnarieIsLoading && (
        <>
          <InfoRow
            title="Require"
            id="requiresSourceAndSizeOfWealth"
            style={{
              position: 'relative',
              zIndex: 17,
            }}
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="requiresSourceAndSizeOfWealth"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Select
                  name={name}
                  placeholder="Select an option..."
                  list={[
                    {
                      name: 'Yes',
                      value: 'yes',
                    },
                    {
                      name: 'No',
                      value: 'no',
                    },
                  ]}
                  value={value as string}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['requiresSourceAndSizeOfWealth']);

                    if (e.target.value === 'no') {
                      questionnarieClearErrors([
                        'sourcesAndSizesOfWealth',
                        'sourcesAndSizesOfWealthOther',
                        'sourcesAndSizesOfWealthBorrowedOther',
                      ]);

                      questionnarieSetValue('sourcesAndSizesOfWealth', []);
                      questionnarieSetValue('sourcesAndSizesOfWealthOther', '');
                      questionnarieSetValue(
                        'sourcesAndSizesOfWealthBorrowedOther',
                        '',
                      );
                    }
                  }}
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? commonStyles.selectDisabled
                      : ''
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={
                    questionnarieErrors?.requiresSourceAndSizeOfWealth?.message
                  }
                  fullWidth
                  borderless
                />
              )}
            />
          </InfoRow>
          {questionnarieFields?.requiresSourceAndSizeOfWealth === 'yes' && (
            <>
              <div className={cn.sourceOfIncomeTitle}>
                Mandatory for high-risk cases only
              </div>
              <div className={cn.sourcesOfIncomeList}>
                {(questionnarieFields?.sourcesAndSizesOfWealth
                  ?.length as number) > 0 &&
                  questionnarieFields?.sourcesAndSizesOfWealth?.map((item) => (
                    <SourceAndSiceOfWealthItem
                      key={item?.id || item.sourceId}
                      sourcesOfWealthForIndividual={sourceOfWealthForIndividual}
                      sourceOfWealthForIndividualFiltered={
                        sourceOfWealthForIndividualFiltered
                      }
                      sourceOfWealthForIndividual={{
                        id: item?.id || item.sourceId,
                        ...item,
                      }}
                      sourceOfWealthForIndividualValues={
                        questionnarieFields?.sourcesAndSizesOfWealth as {
                          sourceId: string;
                          value: string;
                        }[]
                      }
                      questionnarieSetValue={questionnarieSetValue}
                      baseCurrency={baseCurrency}
                      questionnarieControl={questionnarieControl}
                      questionnarieErrors={questionnarieErrors}
                      questionnarieClearErrors={questionnarieClearErrors}
                      isEditable={isEditable}
                      status={status}
                    />
                  ))}
              </div>
              {(questionnarieFields?.sourcesAndSizesOfWealth?.filter(
                (item) => item.sourceId,
              )?.length as number) > 0 && (
                <div className={cn.sourceOfIncomeTotal}>
                  {`Value of Wealth: ${formatBalance(totalValue, baseCurrency)} ${baseCurrency?.name}`}
                </div>
              )}
              {sourceOfWealthForIndividualFiltered &&
                sourceOfWealthForIndividualFiltered?.length > 0 &&
                status !== NewApplicationStatus.ACCEPTED &&
                status !== NewApplicationStatus.DECLINED && (
                  <div className={cn.addSourceButtonWrapper}>
                    <ButtonSecondary
                      text="Add Source"
                      icon={<IconExpand width={16} height={16} />}
                      size={ButtonSize.SMALL}
                      className={`button-white ${cn.addSourceButton}`}
                      onClick={() => {
                        const nextSourcesAndSizesOfWealth = (
                          [] as {
                            sourceId: string;
                            value: string | number;
                            id?: string;
                          }[]
                        ).concat(
                          questionnarieFields?.sourcesAndSizesOfWealth as {
                            id?: string;
                            sourceId: string;
                            value: string | number;
                          }[],
                        );

                        nextSourcesAndSizesOfWealth.push({
                          id: `source-of-wealth-${uuid()}`,
                          sourceId: '',
                          value: '',
                        });

                        questionnarieSetValue(
                          'sourcesAndSizesOfWealth',
                          nextSourcesAndSizesOfWealth,
                        );
                      }}
                    />
                  </div>
                )}
            </>
          )}
          <Controller
            control={questionnarieControl}
            name="sourcesAndSizesOfWealth"
            defaultValue={[]}
            render={({ field: { name } }) => (
              <input type="hidden" name={name} />
            )}
          />
          {questionnarieErrors?.sourcesAndSizesOfWealth?.message && (
            <div className={cn.stepOneQuestionnaireError}>
              {questionnarieErrors?.sourcesAndSizesOfWealth?.message}
            </div>
          )}
        </>
      )}
    </StepPartBlock>
  );
};

export default SourceAndSizeOfWealth;
